// Combined multi-file presentation deck (Unit 10b). A "deck" is an ordered list
// of PDF files; pages are concatenated into one global sequence so the live
// viewer can present "Slide X of N" and step Previous/Next across file
// boundaries. slide_ref stays "{fileId}:{page}" — this module maps between a
// slide_ref position and its global position in the combined deck.

import { SlideRef, parseSlideRef } from './live';

export interface DeckFile {
  fileId: string;
  filename?: string | null;
}

export interface DeckPosition {
  fileId: string;
  page: number;
}

// fileId -> loaded page count. Undefined while the file's PDF is still loading
// (react-pdf reports numPages asynchronously), which the helpers tolerate.
export type PageCounts = Record<string, number | undefined>;

// True for filenames that are presentable PDFs in this phase.
export const isPdfFile = (filename?: string | null): boolean =>
  !!filename && filename.trim().toLowerCase().endsWith('.pdf');

// Resolve the ordered deck files for a live session. When the secretary picked
// a deck (presentation_file_ids), honour that order (filtered to files we know
// about, falling back to a bare entry if a picked file is unknown locally).
// Otherwise fall back to all PDF materials in listing order (legacy behaviour).
export const resolveDeckFiles = (
  presentationFileIds: string[] | null | undefined,
  pdfMaterials: DeckFile[]
): DeckFile[] => {
  if (presentationFileIds && presentationFileIds.length > 0) {
    return presentationFileIds.map((fileId) => {
      const known = pdfMaterials.find((m) => m.fileId === fileId);
      return known || { fileId };
    });
  }
  return [...pdfMaterials];
};

const indexOfFile = (files: DeckFile[], fileId: string): number =>
  files.findIndex((f) => f.fileId === fileId);

// Total slides across the deck (sum of known page counts). complete is false
// while any file's page count is still loading, so callers can show "…".
export const deckTotal = (
  files: DeckFile[],
  counts: PageCounts
): { total: number; complete: boolean } => {
  let total = 0;
  let complete = true;
  for (const f of files) {
    const c = counts[f.fileId];
    if (c == null) complete = false;
    else total += c;
  }
  return { total, complete };
};

// 1-based global slide number of a position, or null when it can't be resolved
// yet (file not in the deck, or a preceding file's page count still loading).
export const deckGlobalIndex = (
  files: DeckFile[],
  counts: PageCounts,
  pos: DeckPosition
): number | null => {
  const i = indexOfFile(files, pos.fileId);
  if (i === -1) return null;
  let sum = 0;
  for (let j = 0; j < i; j++) {
    const c = counts[files[j].fileId];
    if (c == null) return null;
    sum += c;
  }
  return sum + pos.page;
};

// Next position crossing file boundaries, or null at the true end / while the
// current file's page count is still loading (can't tell if we're at its end).
export const deckNext = (
  files: DeckFile[],
  counts: PageCounts,
  pos: DeckPosition
): DeckPosition | null => {
  const i = indexOfFile(files, pos.fileId);
  if (i === -1) return null;
  const count = counts[pos.fileId];
  if (count == null) return null;
  if (pos.page < count) return { fileId: pos.fileId, page: pos.page + 1 };
  if (i + 1 < files.length) return { fileId: files[i + 1].fileId, page: 1 };
  return null;
};

// Previous position crossing file boundaries, or null at the deck start / while
// the previous file's page count is still loading (needed for its last page).
export const deckPrev = (
  files: DeckFile[],
  counts: PageCounts,
  pos: DeckPosition
): DeckPosition | null => {
  const i = indexOfFile(files, pos.fileId);
  if (i === -1) return null;
  if (pos.page > 1) return { fileId: pos.fileId, page: pos.page - 1 };
  if (i - 1 >= 0) {
    const prevCount = counts[files[i - 1].fileId];
    if (prevCount == null) return null;
    return { fileId: files[i - 1].fileId, page: prevCount };
  }
  return null;
};

// Convenience: parse a slide_ref into a DeckPosition (null when not fileId:page).
export const slideRefToPosition = (ref: string | null | undefined): DeckPosition | null => {
  const parsed: SlideRef | null = parseSlideRef(ref);
  return parsed ? { fileId: parsed.fileId, page: parsed.page } : null;
};

// The deck file whose fileId matches, if present (used for the section label).
export const deckFileOf = (files: DeckFile[], fileId: string): DeckFile | null =>
  files.find((f) => f.fileId === fileId) || null;
